import { useMemo, useCallback } from 'react'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import type { UseCharterBundleFallbackPricingParams, CharterBundleFallbackPricingResult, CalendarItemBundleData } from './types'
import { useOfferCharterCalendarData } from './useOfferCharterCalendarData'
import { MALDIVES_BUNDLE_DEFAULT_NIGHTS } from 'constants/charterFlight'

/**
 * Hook to get fallback pricing for charter bundle offers when flight prices are not available
 * Returns the cheapest available bundle pricing from calendar data
 */
export function useCharterBundleFallbackPricing(params: UseCharterBundleFallbackPricingParams): CharterBundleFallbackPricingResult {
  const {
    offer,
    rooms,
    charterBundleRoute,
    fareClass,
    nights = MALDIVES_BUNDLE_DEFAULT_NIGHTS,
    displayMemberPrice = false,
    disabled = false,
    packageUniqueKey,
    feederOriginAirportCode,
  } = params

  // Use default occupancy if rooms is empty to ensure consistent pricing
  const effectiveRoomsForCalendar = useMemo(() => {
    const defaultRoomsForCalendar: Array<App.Occupants> = [{ adults: 2, children: 0, infants: 0, teenagers: 0, childrenAge: [] }]
    return rooms && rooms.length > 0 ? rooms : defaultRoomsForCalendar
  }, [rooms])

  // Get dateRangesByMonth for charter calendar
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: effectiveRoomsForCalendar,
    fareClass,
    disabled: disabled || !charterBundleRoute || effectiveRoomsForCalendar.length === 0,
    feederOriginAirportCode,
  })

  const { itemBundleDataByDate } = useOfferCharterCalendarData({
    offer,
    nights,
    rooms: effectiveRoomsForCalendar,
    fareClass,
    dateRangesByMonth: disabled || !offer ? {} : allDateRangesByMonth,
    charterBundleRoute,
    packageUniqueKey,
    feederOriginAirportCode,
    disabled: disabled || !offer,
  })

  // Find the cheapest bundle entry with its date
  const { cheapestBundleEntry, cheapestDate, cheapestPublicBundleEntry, cheapestMemberBundleEntry } = useMemo(() => {
    if (!charterBundleRoute || Object.keys(itemBundleDataByDate).length === 0) {
      return {
        cheapestBundleEntry: undefined,
        cheapestDate: undefined,
        cheapestPublicBundleEntry: undefined,
        cheapestMemberBundleEntry: undefined,
      }
    }
    const availableEntries = Object.entries(itemBundleDataByDate).filter(
      ([, entry]) => entry.available && entry.totalPrice > 0,
    )
    if (availableEntries.length === 0) {
      return {
        cheapestBundleEntry: undefined,
        cheapestDate: undefined,
        cheapestPublicBundleEntry: undefined,
        cheapestMemberBundleEntry: undefined,
      }
    }
    const [cheapestPublicDate, cheapestPublicBundleEntry] = availableEntries.reduce((cheapest, current) =>
      (current[1].originalTotalPrice ?? current[1].totalPrice) <
        (cheapest[1].originalTotalPrice ?? cheapest[1].totalPrice) ? current : cheapest,
    )
    const [cheapestMemberDate, cheapestMemberBundleEntry] = availableEntries.reduce((cheapest, current) =>
      (current[1].memberTotalPrice ?? current[1].totalPrice) <
        (cheapest[1].memberTotalPrice ?? cheapest[1].totalPrice) ? current : cheapest,
    )
    return displayMemberPrice ? {
      cheapestBundleEntry: cheapestMemberBundleEntry,
      cheapestDate: cheapestMemberDate,
      cheapestPublicBundleEntry,
      cheapestMemberBundleEntry,
    } : {
      cheapestBundleEntry: cheapestPublicBundleEntry,
      cheapestDate: cheapestPublicDate,
      cheapestPublicBundleEntry,
      cheapestMemberBundleEntry,
    }
  }, [charterBundleRoute, displayMemberPrice, itemBundleDataByDate])

  // Find the corresponding date range for the cheapest date (to get checkIn/checkOut and departDate/returnDate)
  const cheapestDateRange = useMemo(() => {
    if (!cheapestDate) return undefined
    for (const ranges of Object.values(allDateRangesByMonth)) {
      const found = ranges.find(range => range.checkIn === cheapestDate)
      if (found) return found
    }
    return undefined
  }, [cheapestDate, allDateRangesByMonth])

  // Build PricingWithValue from bundle data
  // Always return totalPrice, let convertToDisplay handle perPerson conversion
  const buildPricingFromBundle = useCallback((bundleData: CalendarItemBundleData): App.PricingWithValue => {
    const hotelValue = bundleData.hotelValue !== undefined ? bundleData.hotelValue : (bundleData.hotelPrice ?? 0)
    // Calendar data keeps the public total separately from the selected LuxPlus/promotion
    // total. The fallback's base pricing is the non-member display price.
    const totalPrice = bundleData.originalTotalPrice ?? bundleData.totalPrice
    const flightPrice = totalPrice - (bundleData.hotelPrice ?? 0)
    const totalValue = hotelValue + flightPrice // Value = original hotel value + flight price

    // The flight leg has no discount of its own, so compute the % off the hotel's own
    // value vs price rather than the flight-diluted bundle totals. This hook only runs
    // for Maldives charter bundles (guarded by charterBundleRoute).
    const hotelPrice = bundleData.hotelPrice ?? (totalPrice - flightPrice)
    const discountPercent = getValueOffPercent(hotelValue, hotelPrice)

    return {
      price: totalPrice,
      value: totalValue,
      discountPercent,
      fees: [], // Bundle data doesn't include fees breakdown
      saleUnit: offer?.saleUnit ?? 'total',
    }
  }, [offer?.saleUnit])

  // Build fallback pricing
  const fallbackPricing = useMemo(() => {
    if (!cheapestPublicBundleEntry) {
      return undefined
    }
    return buildPricingFromBundle(cheapestPublicBundleEntry)
  }, [cheapestPublicBundleEntry, buildPricingFromBundle])

  // Build fallback member pricing
  // Always return totalPrice, let convertToDisplay handle perPerson conversion
  const fallbackMemberPricing = useMemo(() => {
    if (!cheapestMemberBundleEntry?.memberTotalPrice) {
      return undefined
    }
    const memberTotalPrice = cheapestMemberBundleEntry.memberTotalPrice
    const memberPricingBase = buildPricingFromBundle(cheapestMemberBundleEntry)

    // Member discount is also hotel-only (hotel value vs hotel member price), not
    // the flight-diluted bundle ratio.
    const hotelValue = cheapestMemberBundleEntry.hotelValue ?? cheapestMemberBundleEntry.hotelPrice ?? 0
    const hotelMemberPrice = cheapestMemberBundleEntry.hotelMemberPrice ??
      (memberTotalPrice - (cheapestMemberBundleEntry.flightPrice ?? 0))

    return {
      ...memberPricingBase,
      price: memberTotalPrice,
      value: memberPricingBase.value,
      discountPercent: getValueOffPercent(hotelValue, hotelMemberPrice),
    }
  }, [cheapestMemberBundleEntry, buildPricingFromBundle])

  const rewardHotelPricing = useMemo(() => {
    if (!cheapestBundleEntry) return undefined
    return {
      price: cheapestBundleEntry.hotelPrice,
      memberPrice: cheapestBundleEntry.hotelMemberPrice,
      value: cheapestBundleEntry.hotelValue,
    }
  }, [cheapestBundleEntry])

  return {
    fallbackPricing,
    fallbackMemberPricing,
    rewardHotelPricing,
    cheapestDateRange,
    fallbackHasReturnFareFree: cheapestMemberBundleEntry?.hasReturnFareFree,
  }
}
